import type { ReactNode } from "react";

import { Button } from "../../f7";
import type { Tone } from "../tone";

/** Seconds → the phrasing an operator reads, from `effective_within_seconds`. */
export function effectFromSeconds(seconds: number | undefined, what: string): string {
  if (seconds === undefined) return `${what} takes effect shortly.`;
  if (seconds <= 90) return `${what} reaches merchants within ~${seconds} seconds.`;
  const mins = Math.round(seconds / 60);
  return `${what} reaches merchants within ~${mins} minute${mins === 1 ? "" : "s"}.`;
}

/**
 * A persistent result chip, deliberately not a toast.
 *
 * A toast disappears before the operator has read it, and these messages carry
 * the one fact they most need: this page shows the new value immediately, the
 * merchant may still hit the old one.
 */
export function WriteResult({
  tone = "green",
  title,
  children,
  onDismiss,
}: {
  tone?: Tone;
  title: string;
  children?: ReactNode;
  onDismiss?: () => void;
}) {
  // A ring and `rounded-lg`, the console's card treatment, rather than a
  // `rounded-md` border nothing else here uses.
  const tones: Record<Tone, string> = {
    neutral: "ring-slate-950/5 bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-white/10",
    green: "ring-lime-600/20 bg-lime-400/20 text-lime-700 dark:bg-lime-400/10 dark:text-lime-300",
    amber: "ring-amber-500/20 bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400",
    red: "ring-red-500/20 bg-red-50 text-red-600 dark:bg-red-400/10 dark:text-red-400",
  };
  return (
    <div className={`rounded-lg p-4 text-sm/6 ring-1 ${tones[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{title}</p>
          {children && <div className="mt-1">{children}</div>}
        </div>
        {onDismiss && (
          <div className="shrink-0">
            <Button plain onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
